import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { useI18n } from "./i18n";

const Ctx = createContext(null);
const FAV = "lf_favorites";
const SESSION = "lf_cust_session";
const readFavs = () => { try { return JSON.parse(localStorage.getItem(FAV)) || []; } catch { return []; } };
const writeFavs = (v) => { localStorage.setItem(FAV, JSON.stringify(v)); window.dispatchEvent(new CustomEvent("lf-storage", { detail: FAV })); };

api.defaults.withCredentials = true;
let refreshing = null;
api.interceptors.response.use(undefined, async (err) => {
  const cfg = err.config || {};
  if (err.response?.status === 401 && cfg.url?.startsWith("/customer/") && !cfg._retry && !cfg.url.includes("/refresh") && !cfg.url.includes("/login")) {
    cfg._retry = true;
    try { refreshing = refreshing || api.post("/customer/refresh"); await refreshing; refreshing = null; return api(cfg); } catch { refreshing = null; }
  }
  return Promise.reject(err);
});

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(undefined);
  const syncing = useRef(false);
  const mergeFavs = useCallback(async () => {
    syncing.current = true;
    try { const r = await api.post("/customer/favorites/merge", { ids: readFavs() }); writeFavs(r.data.favorites); } finally { syncing.current = false; }
  }, []);
  useEffect(() => {
    if (!localStorage.getItem(SESSION)) { setCustomer(null); return; }
    api.get("/customer/me").then((r) => { setCustomer(r.data); mergeFavs(); }).catch(() => { localStorage.removeItem(SESSION); setCustomer(null); });
  }, [mergeFavs]);
  useEffect(() => {
    if (!customer) return undefined;
    const onChange = (e) => { if (e.detail === FAV && !syncing.current) api.put("/customer/favorites", { ids: readFavs() }).catch(() => {}); };
    window.addEventListener("lf-storage", onChange);
    return () => window.removeEventListener("lf-storage", onChange);
  }, [customer]);
  const signedIn = (data) => { localStorage.setItem(SESSION, "1"); setCustomer(data); };
  const login = async (email, password) => { const r = await api.post("/customer/login", { email, password }); signedIn(r.data); await mergeFavs(); return r.data; };
  const register = async (body) => { const r = await api.post("/customer/register", body); signedIn(r.data); await mergeFavs(); return r.data; };
  const logout = async () => { localStorage.removeItem(SESSION); await api.post("/customer/logout"); setCustomer(null); };
  return createElement(Ctx.Provider, { value: { customer, login, register, logout } }, children);
}

export const useCustomer = () => useContext(Ctx);

export const useBi = () => {
  const { lang } = useI18n();
  return (en, ar) => (lang === "ar" ? ar : en);
};
